// src/pages/FaqPage.tsx
import { useEffect, useMemo, useState } from "react";

type Faq = {
  id: number;
  category: string;
  question: string;
  answer: string;
  sort_order: number;
};

function catLabel(c: string) {
  const v = String(c || "").toLowerCase();
  if (v === "general") return "일반";
  if (v === "fee") return "수수료";
  if (v === "cancel") return "취소/환불";
  if (v === "payment") return "결제";
  if (v === "contract") return "계약/서류";
  return c || "기타";
}

export default function FaqPage() {
  const [items, setItems] = useState<Faq[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [selectedCat, setSelectedCat] = useState<string>("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setError(null);
        const r = await fetch("/api/faqs");
        const j = await r.json().catch(() => null);
        if (!r.ok || !j?.ok) throw new Error(j?.message || "FAQ 로드 실패");
        setItems(j.items || []);
      } catch (e: any) {
        setError(e?.message || "FAQ 로드 실패");
      }
    })();
  }, []);

  const categories = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of items) {
      const key = it.category || "general";
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = items.filter((it) => {
      const cat = it.category || "general";
      if (selectedCat !== "all" && selectedCat !== cat) return false;
      if (!q) return true;
      const hay = `${it.question} ${it.answer} ${catLabel(cat)}`.toLowerCase();
      return hay.includes(q);
    });
    return list.sort((a, b) => {
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.id - b.id;
    });
  }, [items]);

  return (
    <main className="min-h-[100dvh] bg-slate-50 pb-16">
      <div className="mx-auto max-w-4xl px-4">
        {/* ✅ 상단 여백 최소화 */}
        <div className="pt-2 sm:pt-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
            자주 묻는 질문
          </h1>
          <p className="mt-1 text-sm sm:text-base text-slate-600">
            자주 문의되는 질문들을 먼저 확인해보세요.
          </p>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {!error && items.length === 0 && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
            등록된 FAQ가 없습니다.
          </div>
        )}

        {items.length > 0 && (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedCat("all")}
                className={[
                  "rounded-full px-3 py-1.5 text-xs font-semibold",
                  selectedCat === "all"
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                ].join(" ")}
              >
                전체 ({items.length})
              </button>
              {categories.map(([cat, count]) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelectedCat(cat)}
                  className={[
                    "rounded-full px-3 py-1.5 text-xs font-semibold",
                    selectedCat === cat
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                  ].join(" ")}
                >
                  {catLabel(cat)} ({count})
                </button>
              ))}
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="질문 검색"
              className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
            />
          </div>
        )}

        <div className="mt-5 space-y-3">
          {filtered.map((item) => {
            const opened = openId === item.id;
            return (
              <div
                key={item.id}
                className="rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => setOpenId(opened ? null : item.id)}
                  className="w-full px-4 py-4 text-left"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <span className="mr-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                        {catLabel(item.category || "general")}
                      </span>
                      <span className="text-sm font-medium text-slate-900">
                        {item.question}
                      </span>
                    </div>
                    <span
                      className={[
                        "text-xs text-slate-400 transition",
                        opened ? "rotate-90" : "",
                      ].join(" ")}
                    >
                      ▶
                    </span>
                  </div>
                </button>

                {opened && (
                  <div className="px-4 pb-4 -mt-1">
                    <p className="text-sm text-slate-600 whitespace-pre-line">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {!error && items.length > 0 && filtered.length === 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
              검색 조건에 맞는 FAQ가 없습니다.
            </div>
          )}
        </div>

        <p className="mt-6 text-xs text-slate-500">
          원하는 답변을 찾지 못하셨다면 고객센터의{" "}
          <span className="font-semibold">카카오톡 문의</span>를 이용해 주세요.
        </p>
      </div>
    </main>
  );
}
